import { User } from "firebase/auth";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../Modal";
import StatBox from "./StatBox";
import { sortMembers, getGwRecapNeighbours, GwRecapNeighbour } from "./leaderboard/sort";
import { Member } from "./leaderboard/types";
import { fetchGwRecap, GwRecap, GwRecapBreakdown, LeagueMode } from "../../api/leagues";
import { markGwRecapSeen } from "../../lib/gwRecapSeen";
import { MODE_CONTENT, MODE_COLOR_VAR } from "../../pages/createLeague/modeContent";
import { useIsMobile } from "../../hooks/useIsMobile";
import { errorStyle } from "../../styles";

interface Props {
  user: User;
  leagueId: string;
  mode: LeagueMode;
  myMemberId: string;
  /** Ready members only — same population the visible leaderboard sorts, so neighbours match
   * what the member would see on the table itself. */
  members: Member[];
  /** SYNDICATE's tiebreaker sort needs the tournament's actual goal count — same prop
   * LeaguePage already threads through to the leaderboard. Unused by every other mode. */
  tournamentGoals?: number;
  onClose: () => void;
}

const rowStyle: React.CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "center",
  gap: "0.75rem", padding: "0.5rem 0", borderBottom: "1px solid var(--color-border-subtle)",
  fontSize: "0.86rem",
};

function BreakdownSection({ title, color, children }: { title: string; color: string; children: React.ReactNode }) {
  return (
    <div style={{ background: "var(--color-surface-alt)", borderRadius: 12, padding: "1rem 1.1rem" }}>
      <div style={{
        fontFamily: "var(--font-mono)", fontSize: "0.68rem", fontWeight: 600,
        textTransform: "uppercase", letterSpacing: "0.08em", color, marginBottom: "0.5rem",
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Breakdown({ breakdown, color, isSeasonSummary, t }: { breakdown: GwRecapBreakdown; color: string; isSeasonSummary: boolean; t: (key: string, opts?: Record<string, unknown>) => string }) {
  if (breakdown.type === "TARGET") {
    const finishedLabel = breakdown.targetState === "BUST" ? t("league.targetState.bust")
      : breakdown.targetState === "ELIGIBLE" ? t("league.targetState.eligible")
      : t("league.targetState.ineligible");
    return (
      <BreakdownSection title={isSeasonSummary ? t("gwRecap.target.seasonHeader") : t("gwRecap.target.header")} color={color}>
        {isSeasonSummary ? (
          <p style={{ margin: "0 0 0.6rem", fontSize: "0.82rem", color: "var(--color-text-dim)" }}>
            {t("gwRecap.target.finished", { status: finishedLabel })}
          </p>
        ) : breakdown.becameEligibleThisGw && (
          <p style={{ margin: "0 0 0.6rem", fontSize: "0.82rem", color: "var(--color-success)" }}>
            {t("gwRecap.target.becameEligible")}
          </p>
        )}
        {breakdown.players.map((p) => (
          <div key={p.playerId} style={rowStyle}>
            <span style={{ color: "var(--color-text)" }}>{p.name}</span>
            <span style={{ fontWeight: 700, color: p.points > 0 ? "var(--color-success)" : "var(--color-text-muted)" }}>
              {p.goals} {t(p.goals === 1 ? "gwRecap.target.goal" : "gwRecap.target.goals")}
            </span>
          </div>
        ))}
      </BreakdownSection>
    );
  }

  if (breakdown.type === "FINAL_POSITIONS") {
    const { current, previous } = breakdown;
    const rows: { label: string; value: number }[] = [
      { label: t("gwRecap.fp.base"), value: current.base },
      { label: t("gwRecap.fp.bullseye"), value: current.bullseye },
      { label: t("gwRecap.fp.champion"), value: current.champion },
      { label: t("gwRecap.fp.topTier"), value: current.topTier },
      { label: t("gwRecap.fp.relegation"), value: current.relegation },
    ];
    return (
      <BreakdownSection title={t("gwRecap.fp.header")} color={color}>
        {rows.filter((r) => r.value > 0).map((r) => (
          <div key={r.label} style={rowStyle}>
            <span style={{ color: "var(--color-text)" }}>{r.label}</span>
            <span style={{ fontWeight: 700, color: "var(--color-success)" }}>+{r.value}</span>
          </div>
        ))}
        {!isSeasonSummary && previous === null && (
          <p style={{ margin: "0.6rem 0 0", fontSize: "0.78rem", color: "var(--color-text-dim)" }}>
            {t("gwRecap.fp.noPrevious")}
          </p>
        )}
      </BreakdownSection>
    );
  }

  if (breakdown.type === "GAMEWEEK_CHALLENGE") {
    if (isSeasonSummary) {
      return (
        <BreakdownSection title={t("gwRecap.gwc.seasonHeader")} color={color}>
          <div style={rowStyle}>
            <span style={{ color: "var(--color-text)" }}>{t("gwRecap.gwc.seasonTotal")}</span>
            <span style={{ fontWeight: 700, color: "var(--color-text)" }}>
              {breakdown.seasonTotalGoals ?? 0} {t((breakdown.seasonTotalGoals ?? 0) === 1 ? "gwRecap.target.goal" : "gwRecap.target.goals")}
            </span>
          </div>
          {breakdown.bestPick ? (
            <div style={rowStyle}>
              <span style={{ color: "var(--color-text)" }}>
                {t("gwRecap.gwc.bestPick")}
                <span style={{ color: "var(--color-text-dim)" }}> · {breakdown.bestPick.playerName}
                  {breakdown.bestPick.playerClub && ` (${breakdown.bestPick.playerClub})`} · {breakdown.bestPick.gwName}
                </span>
              </span>
              <span style={{ fontWeight: 700, color: "var(--color-success)" }}>
                {breakdown.bestPick.goals} {t(breakdown.bestPick.goals === 1 ? "gwRecap.target.goal" : "gwRecap.target.goals")}
              </span>
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-dim)" }}>{t("gwRecap.gwc.noPick")}</p>
          )}
        </BreakdownSection>
      );
    }
    return (
      <BreakdownSection title={t("gwRecap.gwc.header")} color={color}>
        {breakdown.playerName ? (
          <div style={rowStyle}>
            <span style={{ color: "var(--color-text)" }}>
              {breakdown.playerName}
              {breakdown.playerClub && <span style={{ color: "var(--color-text-dim)" }}> · {breakdown.playerClub}</span>}
            </span>
            <span style={{ fontWeight: 700, color: breakdown.goals > 0 ? "var(--color-success)" : "var(--color-text-muted)" }}>
              {breakdown.goals} {t(breakdown.goals === 1 ? "gwRecap.target.goal" : "gwRecap.target.goals")}
            </span>
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-dim)" }}>{t("gwRecap.gwc.noPick")}</p>
        )}
      </BreakdownSection>
    );
  }

  if (breakdown.type === "SCORE_PREDICTOR") {
    if (isSeasonSummary) {
      const stats = breakdown.seasonStats;
      return (
        <BreakdownSection title={t("gwRecap.sp.seasonHeader")} color={color}>
          <div style={rowStyle}>
            <span style={{ color: "var(--color-text)" }}>{t("gwRecap.sp.exactScores")}</span>
            <span style={{ fontWeight: 700, color: "var(--color-text)" }}>{stats?.exactScores ?? 0}</span>
          </div>
          <div style={rowStyle}>
            <span style={{ color: "var(--color-text)" }}>{t("gwRecap.sp.correctResults")}</span>
            <span style={{ fontWeight: 700, color: "var(--color-text)" }}>{stats?.correctResults ?? 0}</span>
          </div>
          <div style={rowStyle}>
            <span style={{ color: "var(--color-text)" }}>{t("gwRecap.sp.totalPoints")}</span>
            <span style={{ fontWeight: 700, color: "var(--color-text)" }}>{stats?.points ?? 0}</span>
          </div>
        </BreakdownSection>
      );
    }
    return (
      <BreakdownSection title={t("gwRecap.sp.header")} color={color}>
        {breakdown.fixtures.length === 0 && (
          <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-dim)" }}>{t("gwRecap.sp.noFixtures")}</p>
        )}
        {breakdown.fixtures.map((f) => (
          <div key={f.fixtureId} style={rowStyle}>
            <span style={{ color: "var(--color-text)" }}>
              {f.homeClub} {f.homeScore}-{f.awayScore} {f.awayClub}
              {f.myHomeScore !== null && (
                <span style={{ color: "var(--color-text-dim)" }}> ({t("gwRecap.sp.yourPick", { home: f.myHomeScore, away: f.myAwayScore })})</span>
              )}
            </span>
            <span style={{ fontWeight: 700, color: f.points > 0 ? "var(--color-success)" : "var(--color-text-muted)" }}>
              +{f.points}
            </span>
          </div>
        ))}
      </BreakdownSection>
    );
  }

  // SYNDICATE
  return (
    <BreakdownSection title={t("gwRecap.syndicate.header")} color={color}>
      {breakdown.pots.map((pot) => (
        <div key={pot.potId} style={rowStyle}>
          <span style={{ color: "var(--color-text)" }}>
            {pot.label}
            {pot.pickName && <span style={{ color: "var(--color-text-dim)" }}> · {pot.pickName}</span>}
          </span>
          <span style={{ fontWeight: 700, color: (isSeasonSummary ? pot.points : pot.potDelta) > 0 ? "var(--color-success)" : "var(--color-text-muted)" }}>
            {isSeasonSummary ? pot.points : `${pot.potDelta > 0 ? "+" : ""}${pot.potDelta}`}
          </span>
        </div>
      ))}
    </BreakdownSection>
  );
}

function NeighbourRow({ label, neighbour, t }: { label: string; neighbour: GwRecapNeighbour; t: (key: string, opts?: Record<string, unknown>) => string }) {
  return (
    <div style={rowStyle}>
      <span style={{ color: "var(--color-text-muted)" }}>{label}</span>
      <span style={{ color: "var(--color-text)" }}>
        <strong>{neighbour.displayName}</strong> · {t("gwRecap.neighbours.gap", { count: neighbour.gap })}
      </span>
    </div>
  );
}

export default function GameweekRecapModal({ user, leagueId, mode, myMemberId, members, tournamentGoals, onClose }: Props) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [recap, setRecap] = useState<GwRecap | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchGwRecap(user, leagueId)
      .then((r) => { if (!cancelled) setRecap(r); })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, [user, leagueId]);

  const handleClose = () => {
    if (recap?.available) markGwRecapSeen(user.uid, leagueId, recap.gameweekNumber);
    onClose();
  };

  const content = MODE_CONTENT[mode];
  const color = MODE_COLOR_VAR[mode];
  const Icon = content.icon;

  const neighbours = recap?.available
    ? getGwRecapNeighbours(sortMembers(members, mode, tournamentGoals), myMemberId)
    : { above: null, below: null };

  const title = recap?.available
    ? (recap.isSeasonSummary ? t("gwRecap.seasonTitle") : t("gwRecap.gwTitle", { number: recap.gameweekNumber }))
    : t("gwRecap.eyebrow");

  return (
    <Modal onClose={handleClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--color-surface)",
          borderRadius: 16,
          border: `1px solid color-mix(in srgb, ${color} 25%, var(--color-border))`,
          boxShadow: `0 0 0 1px color-mix(in srgb, ${color} 12%, transparent), 0 24px 64px var(--color-shadow)`,
          maxWidth: 480,
          width: isMobile ? "94%" : "90%",
          maxHeight: isMobile ? "92vh" : "88vh",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            background: `linear-gradient(180deg, color-mix(in srgb, ${color} 18%, var(--color-surface)), var(--color-surface))`,
            borderRadius: "16px 16px 0 0",
            padding: isMobile ? "1.25rem 1.25rem 1.1rem" : "1.5rem 1.75rem 1.25rem",
            position: "relative",
          }}
        >
          <button
            onClick={handleClose}
            aria-label={t("common.close")}
            style={{
              position: "absolute", top: isMobile ? "0.9rem" : "1.1rem", right: isMobile ? "0.9rem" : "1.1rem",
              width: 32, height: 32, borderRadius: 8, border: "1px solid var(--color-border)",
              background: "var(--color-surface)", color: "var(--color-text-muted)", cursor: "pointer",
              fontSize: "1.1rem", lineHeight: 1,
            }}
          >
            ×
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
            <div style={{
              flexShrink: 0, width: 44, height: 44, borderRadius: 12, background: color,
              color: "var(--color-btn-primary-text)", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Icon />
            </div>
            <div>
              <div style={{
                fontFamily: "var(--font-mono)", fontSize: "0.7rem", fontWeight: 600,
                textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--color-text-muted)", marginBottom: "0.15rem",
              }}>
                {t(recap?.available && recap.isSeasonSummary ? "gwRecap.seasonEyebrow" : "gwRecap.eyebrow")}
              </div>
              <h3 style={{
                margin: 0, fontFamily: "var(--font-display)", fontSize: isMobile ? "1.3rem" : "1.6rem",
                fontWeight: 700, color: "var(--color-text)",
              }}>
                {title}
              </h3>
            </div>
          </div>
        </div>

        <div style={{ padding: isMobile ? "1.25rem" : "1.5rem 1.75rem 1.75rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
          {!recap && !loadError && (
            <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--color-text-muted)" }}>{t("common.loading")}</p>
          )}
          {loadError && <p style={errorStyle}>{t("gwRecap.error")}</p>}
          {recap && !recap.available && (
            <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--color-text-muted)" }}>
              {t(`gwRecap.unavailable.${recap.reason}`)}
            </p>
          )}
          {recap?.available && (
            <>
              <div style={{ display: "flex", gap: "0.75rem" }}>
                <StatBox label={t("gwRecap.score")} value={recap.myScore} accent borderColor={color} />
                {recap.isSeasonSummary ? (
                  <StatBox
                    label={t("gwRecap.finalRank")}
                    value={recap.finalRank != null ? `#${recap.finalRank}` : "—"}
                    sub={recap.totalReadyMembers != null ? t("gwRecap.ofN", { n: recap.totalReadyMembers }) : undefined}
                    accent
                    borderColor={color}
                  />
                ) : (
                  <StatBox
                    label={t("gwRecap.thisGameweek")}
                    value={`${recap.myDelta > 0 ? "+" : ""}${recap.myDelta}`}
                    success={recap.myDelta > 0}
                    borderColor={color}
                  />
                )}
              </div>

              <Breakdown breakdown={recap.breakdown} color={color} isSeasonSummary={recap.isSeasonSummary} t={t} />

              {(neighbours.above || neighbours.below) && (
                <BreakdownSection title={t(recap.isSeasonSummary ? "gwRecap.neighbours.seasonHeader" : "gwRecap.neighbours.header")} color={color}>
                  {neighbours.above
                    ? <NeighbourRow label={t("gwRecap.neighbours.above")} neighbour={neighbours.above} t={t} />
                    : <p style={{ margin: "0.3rem 0", fontSize: "0.82rem", color: "var(--color-success)" }}>{t(recap.isSeasonSummary ? "gwRecap.neighbours.seasonLeading" : "gwRecap.neighbours.leading")}</p>}
                  {neighbours.below
                    ? <NeighbourRow label={t("gwRecap.neighbours.below")} neighbour={neighbours.below} t={t} />
                    : <p style={{ margin: "0.3rem 0 0", fontSize: "0.82rem", color: "var(--color-text-dim)" }}>{t(recap.isSeasonSummary ? "gwRecap.neighbours.seasonLast" : "gwRecap.neighbours.last")}</p>}
                </BreakdownSection>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
